import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Shield, FileText, Building2, Search, ShoppingCart, Award, Zap, CheckCircle2, DollarSign, MessageCircle, ArrowRight, Star, Loader2 } from 'lucide-react';
import { services, testimonials, faqs, benefits } from '../mockData';
import ChatPopup from '../components/ChatPopup';
import RegistrationPopup from '../components/RegistrationPopup';
import PricingSection from '../components/PricingSection';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { seoConfig } from '../utils/seoConfig';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { countryCodes } from '../constants/countryCodes';
import { getGeoLocation, submitLead } from '../utils/api';

const iconMap = {
  Shield,
  FileText,
  Building2,
  Search,
  ShoppingCart,
  Award,
  Zap,
  CheckCircle2,
  DollarSign,
  MessageCircle
};

const Home = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [tmSignUpType, setTmSignUpType] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState({ title: 'Basic', amount: 45 });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const servicesRef = React.useRef(null);
  const benefitsRef = React.useRef(null);
  const testimonialsRef = React.useRef(null);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });


  React.useEffect(() => {
    const fetchGeoLocation = async () => {
      const geoResult = await getGeoLocation();
      if (geoResult.success) {
        setGeoData(geoResult);
        setFormData(prev => ({
          ...prev,
          countryCode: `+${geoResult.dialCode}`
        }));
      }
    };

    fetchGeoLocation();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'contactNumber') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setFormData({ ...formData, [name]: numericValue });
      }
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.message || !formData.message.trim()) {
      toast.error('Please describe what you are looking to protect.');
      return;
    }
    setLoading(true);
    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.contactNumber,
        countryDialCode: parseInt(formData.countryCode.replace('+', '')),
        country: geoData?.countryCode || 'US',
        description: formData.message,
        ipAddress: geoData?.ipAddress || '',
        pageUrl: window.location.href,
        city: geoData?.city || '',
        state: geoData?.state || '',
        geoLocation: geoData?.fullResponse || null,
        packageTitle: 'Basic',
        packageAmount: 45,
        tmSignUpType: 1
      };

      const result = await submitLead(leadData);
      if (result.success) {
        toast.success(result.message || 'Thank you! Redirecting...');
        setFormData({ name: '', email: '', countryCode: '+1', contactNumber: '', message: '' });
        setTimeout(() => {
          // navigate(`/application/step-one?customerid=${result.customerId}`);
          window.location.href = '/application/main?customerid=' + result.customerId;
        }, 1000);
      } else {
        toast.error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-slide testimonials (for desktop - changes content every 5 seconds)
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (window.innerWidth >= 768) {
        setCurrentTestimonial((prev) => (prev + 1) % Math.ceil(testimonials.length / 3));
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll testimonials slider on mobile
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (testimonialsRef.current && window.innerWidth < 768) {
        const scrollWidth = testimonialsRef.current.scrollWidth;
        const clientWidth = testimonialsRef.current.clientWidth;
        const currentScroll = testimonialsRef.current.scrollLeft;
        const cardWidth = clientWidth * 0.85;
        
        if (currentScroll + clientWidth >= scrollWidth - 10) {
          testimonialsRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          testimonialsRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
        }
      }
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll services slider on mobile
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (servicesRef.current && window.innerWidth < 768) {
        const scrollWidth = servicesRef.current.scrollWidth;
        const clientWidth = servicesRef.current.clientWidth;
        const currentScroll = servicesRef.current.scrollLeft;
        const cardWidth = clientWidth * 0.85;
        
        if (currentScroll + clientWidth >= scrollWidth - 10) {
          servicesRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          servicesRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
        }
      }
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll benefits slider on mobile
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (benefitsRef.current && window.innerWidth < 768) {
        const scrollWidth = benefitsRef.current.scrollWidth;
        const clientWidth = benefitsRef.current.clientWidth;
        const currentScroll = benefitsRef.current.scrollLeft;
        const cardWidth = clientWidth * 0.85;
        
        if (currentScroll + clientWidth >= scrollWidth - 10) {
          benefitsRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          benefitsRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
        }
      }
    }, 4500); // Change every 4.5 seconds

    return () => clearInterval(interval);
  }, []);

  // Get testimonials to display (3 at a time)
  const displayTestimonials = () => {
    const startIndex = currentTestimonial * 3;
    return testimonials.slice(startIndex, startIndex + 3);
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <SEO 
        title={seoConfig.home.title}
        description={seoConfig.home.description}
        keywords={seoConfig.home.keywords}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-40 w-80 h-80 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4">
                <span className="bg-gradient-to-r from-orange-600 to-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg">
                  â­ Trusted by 5,000+ Businesses
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-600 via-orange-500 to-amber-600">
                  Protect Your Brand
                </span>
                <br />
                <span className="text-gray-900">with Confidence</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Expert trademark and intellectual property services to secure your business identity and drive growth with cutting-edge legal protection.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={() => setIsRegistrationOpen(true)} size="lg" className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all">
                  Register My Trademark <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button onClick={() => window.openLiveChat && window.openLiveChat()} size="lg" variant="outline" className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 shadow-lg">
                  <MessageCircle className="mr-2 h-5 w-5" /> Talk to an Expert Now
                </Button>
              </div>
            </div>
            <Card className="border-0 shadow-2xl mt-8 lg:mt-0">
              <CardContent className="pt-6 md:pt-8 px-4 md:px-6">
                <div className="text-center mb-4 md:mb-6">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Start Your Registration Today</h3>
                  <p className="text-sm md:text-base text-gray-600">Get consultation with our trademark experts</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactNumber">Contact Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        value={formData.countryCode}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, countryCode: value }))}
                      >
                        <SelectTrigger className="w-28">
                          {countryCodes.find(c => c.code === formData.countryCode) ? `${countryCodes.find(c => c.code === formData.countryCode).abbr} ${countryCodes.find(c => c.code === formData.countryCode).code}` : 'US +1'}
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px] overflow-y-auto">
                          {countryCodes.map((item) => (
                            <SelectItem key={item.code} value={item.code}>
                              {item.abbr} {item.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="contactNumber"
                        name="contactNumber"
                        type="tel"
                        required
                        value={formData.contactNumber}
                        onChange={handleChange}
                        placeholder="1234567890"
                        maxLength={10}
                        pattern="[0-9]{10}"
                        title="Please enter exactly 10 digits"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">What are you looking to protect?</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)"
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 text-white" size="lg">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Register My Trademark'
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    By submitting, you agree to receive calls and messages about our services.
                  </p>
                </form>
              </CardContent>
            </Card>
          
          
          
          </div>
        </div>
      </section>

      {/* About Trademark Services Section */}
      <section className="py-14 bg-gradient-to-br from-orange-600 to-amber-700 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-white">
              <div className="inline-block mb-4">
                <span className="bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-4 py-2 rounded-full border border-white/30">
                  Why Trademark Protection Matters
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">
                Your Brand is Your Legacy. <br />
                <span className="text-orange-100">Protect It Today.</span>
              </h2>
              <p className="text-lg text-orange-50 mb-6 leading-relaxed">
                In today's competitive marketplace, your trademark is more than just a logo or nameâ€”it's the identity of your business, the trust you've built with customers, and your competitive advantage.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/30">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base mb-1">Exclusive Ownership Rights</h3>
                    <p className="text-sm text-orange-100">Gain nationwide protection and legal presumption of ownership for your brand identity.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/30">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base mb-1">Build Brand Value</h3>
                    <p className="text-sm text-orange-100">A registered trademark increases your company's value and becomes a powerful business asset.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/30">
                    <CheckCircle2 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base mb-1">Legal Protection</h3>
                    <p className="text-sm text-orange-100">Stop competitors from using similar marks and take legal action against infringement.</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setIsChatOpen(true)} 
                size="lg" 
                className="bg-white text-orange-600 hover:bg-orange-50 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all font-semibold"
              >
                Start Your Trademark Journey <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Right Side - Benefit Cards */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-5">
                {/* Card 1 - Prevent Infringement */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all transform hover:scale-105 hover:shadow-2xl min-h-[180px] flex flex-col justify-between">
                  <div>
                    <div className="text-4xl mb-3">🛡️</div>
                    <div className="text-white font-semibold text-lg mb-2">Prevent Infringement</div>
                  </div>
                  <div className="text-sm text-orange-50 leading-relaxed">Stop competitors from copying your brand name, logo, or products. A registered trademark gives you the exclusive right to use your mark and take legal action against unauthorized use.</div>
                </div>

                {/* Card 2 - Legal Authority */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all transform hover:scale-105 hover:shadow-2xl min-h-[180px] flex flex-col justify-between">
                  <div>
                    <div className="text-4xl mb-3">⚖️</div>
                    <div className="text-white font-semibold text-lg mb-2">Legal Authority</div>
                  </div>
                  <div className="text-sm text-orange-50 leading-relaxed">Sue infringers in federal court and recover damages. Your registered trademark provides strong legal standing and the ability to enforce your rights nationwide.</div>
                </div>

                {/* Card 3 - Domain Protection */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all transform hover:scale-105 hover:shadow-2xl min-h-[180px] flex flex-col justify-between">
                  <div>
                    <div className="text-4xl mb-3">🌐</div>
                    <div className="text-white font-semibold text-lg mb-2">Domain Protection</div>
                  </div>
                  <div className="text-sm text-orange-50 leading-relaxed">Safeguard against domain hijacking and cybersquatting. Trademark registration helps you reclaim domain names that infringe on your brand and protects your online presence.</div>
                </div>

                {/* Card 4 - Business Asset */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all transform hover:scale-105 hover:shadow-2xl min-h-[180px] flex flex-col justify-between">
                  <div>
                    <div className="text-4xl mb-3">💎</div>
                    <div className="text-white font-semibold text-lg mb-2">Valuable Asset</div>
                  </div>
                  <div className="text-sm text-orange-50 leading-relaxed">Increases company valuation significantly. Trademarks are intangible assets that can be licensed, sold, or used as collateral. They grow more valuable over time.</div>
                </div>
              </div>

              {/* Floating Shield Icon */}
              <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 backdrop-blur-lg rounded-3xl flex items-center justify-center border border-white/20 transform rotate-12 hover:rotate-0 transition-transform shadow-2xl">
                <Shield className="h-14 w-14 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-100 rounded-full -translate-y-32 translate-x-32 opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-100 rounded-full translate-y-32 -translate-x-32 opacity-50"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-amber-600">
                Our Services
              </span>
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Comprehensive trademark and intellectual property services to protect your brand
            </p>
          </div>
          <div ref={servicesRef} className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto gap-6 snap-x snap-mandatory scrollbar-hide pb-8 pt-4 -mx-4 px-4 md:mx-0 md:px-0">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              const gradients = [
                'from-orange-500 to-amber-500',
                'from-amber-500 to-orange-600',
                'from-orange-600 to-orange-500',
                'from-orange-500 to-amber-600',
                'from-amber-600 to-orange-500',
                'from-orange-600 to-amber-500'
              ];
              const gradient = gradients[index % gradients.length];
              
              return (
                <Card key={service.id} className="group relative transition-all duration-300 border-2 border-gray-200 bg-white hover:border-orange-400 hover:-translate-y-2 hover:shadow-xl snap-center flex-shrink-0 w-[85vw] md:w-auto">
                  <CardHeader>
                    <div className={`w-16 h-16 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center mb-4 shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors duration-300">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-gray-600 line-clamp-2">
                      {service.shortDesc}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild variant="ghost" className="group/btn text-orange-600 hover:text-orange-700 hover:bg-orange-50 p-0 transition-all duration-300">
                      <Link to={service.link} className="flex items-center">
                        Learn More 
                        <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-2 transition-transform duration-300" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* CTA Box */}
            <Card className="group relative transition-all duration-300 border-2 border-orange-400 bg-gradient-to-br from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 hover:-translate-y-2 hover:shadow-xl snap-center flex-shrink-0 w-[85vw] md:w-auto flex items-center justify-center">
              <CardContent className="text-center py-12 px-6">
                <div className="mb-6">
                  <h3 className="text-3xl font-bold text-white mb-3">
                    Get Started Today
                  </h3>
                  <p className="text-orange-50 text-sm">
                    Have questions? Let's chat about protecting your brand.
                  </p>
                </div>
                <Button 
                  onClick={() => setIsChatOpen(true)} 
                  size="lg" 
                  className="bg-white text-orange-600 hover:bg-orange-50 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all font-semibold"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Chat with Expert
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Trademark Upgrades</h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Trusted by thousands of businesses for professional trademark services
            </p>
          </div>
          <div ref={benefitsRef} className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto gap-6 snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 md:mx-0 md:px-0">
            {benefits.map((benefit, index) => {
              const Icon = iconMap[benefit.icon];
              return (
                <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow snap-center flex-shrink-0 w-[85vw] md:w-auto min-w-0">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner Section */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-br from-orange-600 via-orange-500 to-amber-600">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-white rounded-full translate-x-1/3 translate-y-1/3 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-amber-300 rounded-full blur-2xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-400 rounded-full blur-3xl"></div>
        </div>
        
        {/* Decorative Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-48 h-48 border-4 border-white/20 rounded-full"></div>
          <div className="absolute top-1/2 -left-16 w-32 h-32 border-4 border-white/20 rounded-full"></div>
          <div className="absolute bottom-16 right-1/4 w-24 h-24 bg-white/10 rotate-45"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Protect Your Brand Today
              </h2>
              <p className="text-lg md:text-xl text-orange-50 leading-relaxed mb-8">
                Don't leave your brand vulnerable to copycats and infringement. Our expert team has helped thousands of businesses secure their trademarks quickly and affordably. Start your trademark registration journey now and safeguard your intellectual property with confidence.
              </p>
            </div>

            {/* Right Buttons */}
            <div className="flex flex-col space-y-4 lg:items-end">
              <Button 
                size="lg" 
                onClick={() => setIsChatOpen(true)}
                className="bg-white text-orange-600 hover:bg-gray-100 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all font-semibold text-lg px-8 py-6 w-full lg:w-auto"
              >
                Register My Trademark <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                onClick={() => setIsChatOpen(true)}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 shadow-xl hover:shadow-2xl transition-all font-semibold text-lg px-8 py-6 w-full lg:w-auto backdrop-blur-sm"
              >
                <MessageCircle className="mr-2 h-5 w-5" /> Talk to an Expert Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection onOpenRegistration={(tmSignUpType, packageTitle, packageAmount) => {
        setTmSignUpType(tmSignUpType);
        setSelectedPackage({ title: packageTitle, amount: packageAmount });
        setIsRegistrationOpen(true);
      }} />

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-base text-orange-100 max-w-3xl mx-auto">
              Don't just take our word for it - hear from businesses we've helped protect
            </p>
          </div>
          <div className="relative">
            {/* Desktop: Show 3 at a time with auto-change */}
            <div className="hidden md:grid md:grid-cols-3 gap-8 transition-all duration-500">
              {displayTestimonials().map((testimonial) => (
                <Card key={testimonial.id} className="bg-white/10 backdrop-blur-sm border-white/20 transform transition-all duration-500 hover:scale-105">
                  <CardContent className="pt-6">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-white mb-6 italic">"{testimonial.content}"</p>
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-sm text-orange-100">{testimonial.role}, {testimonial.company}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Mobile: Horizontal slider with all testimonials */}
            <div ref={testimonialsRef} className="md:hidden flex overflow-x-auto gap-6 snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="bg-white/10 backdrop-blur-sm border-white/20 snap-center flex-shrink-0 w-[85vw]">
                  <CardContent className="pt-6">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-white mb-6 italic">"{testimonial.content}"</p>
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-sm text-orange-100">{testimonial.role}, {testimonial.company}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Slider Indicators - Desktop only */}
            <div className="hidden md:flex justify-center gap-2 mt-8">
              {[...Array(Math.ceil(testimonials.length / 3))].map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    currentTestimonial === index ? 'bg-white w-8' : 'bg-white/50'
                  }`}
                  aria-label={`Go to testimonial slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-base text-gray-600">
              Find answers to common questions about trademark registration
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-white border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Your Brand Deserves Legal Protection
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Don't wait until it's too late. Secure your trademark rights today and gain exclusive nationwide protection for your brand identity. Our expert attorneys make the registration process seamless and stress-free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setIsChatOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
              Register My Trademark <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" onClick={() => setIsChatOpen(true)} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
              <MessageCircle className="mr-2 h-4 w-4" /> Talk to an Expert Now
            </Button>
          </div>
        </div>
      </section>

      {/* Chat Popup */}
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
      <RegistrationPopup isOpen={isRegistrationOpen} onClose={() => setIsRegistrationOpen(false)} tmSignUpType={tmSignUpType} packageTitle={selectedPackage.title} packageAmount={selectedPackage.amount} />
    </div>
  );
};

export default Home;
